import React, { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../LanguageContext';
import { useAuth } from '../AuthContext';
import { projLabel } from '../components/UnitsDeals/constants';
import { holdPool, releaseHold, transitionLeadStatus } from '../api';

// Parked / "on hold" leads pool — customers postponed for an external reason (bank,
// government docs, pricing not set). Grouped by reason so management sees systemic
// blockers; admin/manager can release a whole reason-group when the blocker clears,
// or reactivate one lead. Sales see their own parked leads and can reactivate them.
export const HOLD_REASONS = {
  waiting_pricing: ['الأسعار غير محددة', 'Pricing not set'],
  waiting_bank: ['بانتظار البنك', 'Waiting on bank'],
  waiting_gov: ['بانتظار أوراق حكومية', 'Waiting on gov. docs'],
  other: ['أخرى', 'Other'],
};
const REASON_COLOR = { waiting_pricing: '#f59e0b', waiting_bank: '#3b82f6', waiting_gov: '#8b5cf6', other: '#94a3b8' };

export default function HoldPool() {
  const { lang, isRTL, t: tr } = useLanguage();
  const { user } = useAuth();
  const ar = lang === 'ar';
  const t = (a, e) => (ar ? a : e);
  const isMgr = user?.role === 'admin' || user?.role === 'manager';
  const [data, setData] = useState({ leads: [], groups: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const rLabel = (r) => (HOLD_REASONS[r] ? t(HOLD_REASONS[r][0], HOLD_REASONS[r][1]) : r);
  const pLabel = (p) => projLabel(p, tr) || p || '—';

  const load = useCallback(async () => {
    setLoading(true);
    const r = await holdPool();
    setData(r.error ? { leads: [], groups: [] } : r);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const releaseGroup = async (reason, project) => {
    const label = rLabel(reason) + (project ? ` · ${pLabel(project)}` : '');
    if (!window.confirm(t(`إرجاع كل العملاء المؤجلين لسبب «${label}» إلى المبيعات؟`, `Release all leads held for "${label}" back to sales?`))) return;
    setBusy(true);
    const r = await releaseHold({ reason, project });
    setBusy(false);
    if (r.error) { window.alert(t('تعذّر الإرجاع', 'Release failed')); return; }
    window.alert(t(`تمت إعادة ${r.released} عميل`, `${r.released} lead(s) released`));
    load();
  };

  const reactivateOne = async (lead) => {
    setBusy(true);
    const r = isMgr
      ? await releaseHold({ id: lead.id })
      : await transitionLeadStatus(lead.id, { toStatus: lead.holdPrevStatus && !['on_hold', 'won', 'lost'].includes(lead.holdPrevStatus) ? lead.holdPrevStatus : 'contacted', outcome: 'reactivate', statusNote: t('إعادة تفعيل من قائمة المؤجلين', 'Reactivated from the hold pool') });
    setBusy(false);
    if (r && r.error) { window.alert(t('تعذّر إعادة التفعيل', 'Reactivate failed')); return; }
    load();
  };

  const fmtDate = (d) => { if (!d) return null; try { return new Date(d).toLocaleDateString(ar ? 'ar-SA' : 'en-GB'); } catch { return null; } };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 1040, margin: '0 auto', padding: '4px 4px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <span style={{ fontSize: '1.2rem' }}>⏸️</span>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0 }}>{t('العملاء المؤجلون', 'Parked leads (on hold)')}</h2>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{data.leads.length}</span>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>…</div>
      ) : data.leads.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('لا يوجد عملاء مؤجلون', 'No parked leads')}</div>
      ) : (
        <>
          {/* Reason groups — management sees systemic blockers + release */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
            {data.groups.map((g) => {
              const c = REASON_COLOR[g.reason] || '#94a3b8';
              return (
                <div key={g.reason} style={{ flex: '1 1 220px', minWidth: 220, border: '1px solid var(--border)', borderInlineStart: `3px solid ${c}`, borderRadius: 12, padding: '12px 14px', background: 'var(--bg-secondary)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <b style={{ color: c }}>{rLabel(g.reason)}</b>
                    <span style={{ fontSize: '1.2rem', fontWeight: 800 }}>{g.total}</span>
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {Object.entries(g.projects).sort((a, b) => b[1] - a[1]).map(([proj, n]) => (
                      <div key={proj} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: '0.78rem' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>🏢 {pLabel(proj)} · {n}</span>
                        {isMgr && <button disabled={busy} onClick={() => releaseGroup(g.reason, proj)} style={{ fontSize: '0.68rem', fontWeight: 700, padding: '3px 8px', borderRadius: 6, border: `1px solid ${c}`, background: c + '14', color: c, cursor: 'pointer' }}>↩ {t('إرجاع', 'Release')}</button>}
                      </div>
                    ))}
                  </div>
                  {isMgr && <button disabled={busy} onClick={() => releaseGroup(g.reason, null)} style={{ marginTop: 10, width: '100%', fontSize: '0.74rem', fontWeight: 800, padding: '6px', borderRadius: 8, border: 'none', background: c, color: '#fff', cursor: 'pointer' }}>↩ {t('إرجاع كل هذا السبب', 'Release all for this reason')}</button>}
                </div>
              );
            })}
          </div>

          {/* The parked leads */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.leads.map((l) => {
              const c = REASON_COLOR[l.holdReason] || '#94a3b8';
              const until = fmtDate(l.holdUntil);
              return (
                <div key={l.id} style={{ border: '1px solid var(--border)', borderInlineStart: `3px solid ${c}`, borderRadius: 10, padding: '10px 14px', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <b style={{ fontSize: '0.9rem' }}>{l.name}</b>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginInlineStart: 8, direction: 'ltr' }}>{l.phone}</span>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ color: c, fontWeight: 700 }}>⏸ {rLabel(l.holdReason)}</span>
                      {l.project && <span>🏢 {pLabel(l.project)}</span>}
                      {l.assignedTo && <span>🛠️ {l.assignedTo}</span>}
                      {until && <span>🔔 {t('يعود في', 'back on')} {until}</span>}
                      {l.holdNote && <span style={{ fontStyle: 'italic' }}>“{l.holdNote}”</span>}
                    </div>
                  </div>
                  <button disabled={busy} onClick={() => reactivateOne(l)} style={{ fontSize: '0.76rem', fontWeight: 800, padding: '6px 14px', borderRadius: 8, border: '1.5px solid #22c55e', background: '#22c55e14', color: '#16a34a', cursor: 'pointer', whiteSpace: 'nowrap' }}>↩ {t('إعادة تفعيل', 'Reactivate')}</button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
